import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faTimes,
  faChevronRight,
  faChevronLeft,
  faGraduationCap,
  faClock,
  faList,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import Loader from "../Shared/Loader";
import ApiServices from "../Services/ApiServices";
import "../../assets/css/ExamResult.css";
import { decryptId } from "../../utils/encryption";
import Swal from "sweetalert2";
import extractErrorMessage from "../../Helpers/APIHelper";
import { toastHelper } from '../../Helpers/toastHelper';
import { AppKeys } from "../Shared/AppKeys";

const ExamResult = () => {
  const { id: encryptedId } = useParams();
  const navigate = useNavigate();
  const [examData, setExamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [examId, setExamId] = useState(null);
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [examLanguage, setExamLanguage] = useState('_Arabic'); // Default to Arabic
  const [textDirection, setTextDirection] = useState('rtl'); // Default to RTL

  // Helper to determine language and direction based on material
  const determineLanguageAndDirection = (material) => {
    if (material === AppKeys.materials._English || material === AppKeys.materials._French) {
      return {
        language: material === AppKeys.materials._English ? '_English' : '_French',
        direction: 'ltr',
        isRTL: false
      };
    } else {
      // Default to Arabic for any other material
      return {
        language: '_Arabic',
        direction: 'rtl',
        isRTL: true
      };
    }
  };

  // Text content in different languages
  const examTexts = {
    _Arabic: {
      loading: "جاري تحميل نتائج الاختبار...",
      examNotFound: "لم يتم العثور على نتائج الاختبار",
      backToExams: "العودة إلى قائمة الاختبارات",
      teacher: "المدرس",
      duration: "المدة",
      notSpecified: "غير محدد",
      finalResult: "النتيجة النهائية",
      questionNavigation: "تنقل الأسئلة",
      question: "السؤال",
      from: "من",
      noQuestionText: "لا يوجد نص للسؤال",
      noOptions: "لا توجد خيارات متاحة لهذا السؤال",
      next: "التالي",
      previous: "السابق",
      correctAnswer: "إجابة صحيحة",
      incorrectAnswer: "إجابة خاطئة",
      notAnswered: "لم يتم الإجابة",
      questionWeight: "وزن السؤال",
      questionWeightHint: "كل سؤال يساوي {weight} نقطة",
      error: "خطأ",
      invalidLink: "رابط النتيجة غير صالح",
      errorLoadingResults: "حدث خطأ أثناء تحميل نتائج الاختبار"
    },
    _English: {
      loading: "Loading exam results...",
      examNotFound: "Exam results not found",
      backToExams: "Back to exam list",
      teacher: "Teacher",
      duration: "Duration",
      notSpecified: "Not specified",
      finalResult: "Final Result",
      questionNavigation: "Question Navigation",
      question: "Question",
      from: "of",
      noQuestionText: "No question text available",
      noOptions: "No options available for this question",
      next: "Next",
      previous: "Previous",
      correctAnswer: "Correct Answer",
      incorrectAnswer: "Incorrect Answer",
      notAnswered: "Not Answered",
      questionWeight: "Question Weight",
      questionWeightHint: "Each question is worth {weight} points",
      error: "Error",
      invalidLink: "Invalid result link",
      errorLoadingResults: "Error loading exam results"
    }
  };

  // Helper to get text based on current language
  const getText = (key, replacement = null) => {
    const text = examTexts[examLanguage]?.[key] || examTexts._Arabic[key];
    if (replacement !== null) {
      if (text.includes('{count}')) {
        return text.replace('{count}', replacement);
      }
      if (text.includes('{weight}')) {
        return text.replace('{weight}', replacement);
      }
    }
    return text;
  };

  // Add effect to handle decryption
  useEffect(() => {
    try {
      if (!encryptedId) {
        throw new Error("No exam ID provided");
      }

      const decryptedId = decryptId(encryptedId);

      if (!decryptedId) {
        throw new Error("Invalid exam ID");
      }

      setExamId(decryptedId);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: getText("error"),
        text: getText("invalidLink"),
      }).then(() => {
        navigate("/myexam");
      });
    }
  }, [encryptedId, navigate]);

  useEffect(() => {
    const fetchExamResult = async () => {
      if (!examId) return;

      try {
        const response = await ApiServices.get(
          `UserExam/Details/${encryptedId}`
        );
        if (response.check) {
          const data = response.data;
          setExamData(data);

          // Determine language and direction based on material
          if (data && data.length > 0 && data[0].material) {
            const { language, direction, isRTL } = determineLanguageAndDirection(data[0].material);
            setExamLanguage(language);
            setTextDirection(direction);
          }

          // Calculate score - only count questions that were actually answered
          const correctAnswers = data.filter(
            (q) => q.selected_answer !== null &&
              q.selected_answer !== undefined &&
              q.selected_answer !== 0 &&
              Number(q.selected_answer) === Number(q.correct_answer)
          ).length;
          setScore({
            correct: correctAnswers,
            total: data.length,
          });
        } else {
          throw new Error(getText("errorLoadingResults"));
        }
      } catch (error) {
        const title = error?.title || error?.message || "";
        if (title.toLowerCase().includes("validation errors")) {
          const errMessage = extractErrorMessage(error);
          toastHelper.error(errMessage);
        } else {
          toastHelper.error(
            error || error?.msg || getText("errorLoadingResults")
          );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchExamResult();
  }, [examId, navigate]);

  const handleNext = () => {
    if (currentQuestion < examData.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    }
  };

  const getQuestionStatus = (question) => {
    const hasAnswer = question.selected_answer !== null &&
      question.selected_answer !== undefined &&
      question.selected_answer !== 0;

    if (!hasAnswer) {
      return "unanswered";
    }
    return Number(question.selected_answer) === Number(question.correct_answer) ? "correct" : "incorrect";
  };

  const getAnsweredCount = () => {
    return examData?.filter(q =>
      q.selected_answer !== null &&
      q.selected_answer !== undefined &&
      q.selected_answer !== 0
    ).length || 0;
  };

  const handleQuestionSelect = (questionIndex) => {
    setCurrentQuestion(questionIndex);
    setShowNavigationModal(false);
  };

  const toggleNavigationModal = () => {
    setShowNavigationModal(!showNavigationModal);
  };

  if (loading) {
    return <Loader text={getText("loading")} />;
  }

  if (!examData) {
    return (
      <div className="exam-error" style={{ direction: textDirection }}>
        <h3>{getText("examNotFound")}</h3>
        <button onClick={() => navigate("/myexam")}>
          {getText("backToExams")}
        </button>
      </div>
    );
  }

  const currentQuestionData = examData[currentQuestion];

  // Safely parse options with error handling
  let options = [];
  try {
    if (currentQuestionData && currentQuestionData.options) {
      options = JSON.parse(currentQuestionData.options);
    }
  } catch (error) {
    
    options = [];
  }

  const answeredCount = getAnsweredCount();
  const progressPercentage = Math.round((answeredCount / examData.length) * 100);

  return (
    <div className="exam-result" style={{ direction: textDirection }}>
      <div className="result-card">
        {/* Header Section */}
        <div className="result-header">
          <div className="exam-info">
            <h2>{currentQuestionData?.title || "نتيجة الاختبار"}</h2>
            <div className="teacher-info">
              <FontAwesomeIcon icon={faGraduationCap} />
              <span>{getText("teacher")}: {currentQuestionData?.teacher || getText("notSpecified")}</span>
            </div>
            <div className="duration-info">
              <FontAwesomeIcon icon={faClock} />
              <span>{getText("duration")}: {currentQuestionData?.duration || getText("notSpecified")}</span>
            </div>
          </div>

          <div className="score-info">
            <h3>{getText("finalResult")}</h3>
            <div className="score">
              <span className="correct">{score.correct}</span>
              <span className="divider">/</span>
              <span className="total">{score.total}</span>
            </div>
            <div className="percentage">
              {Math.round((score.correct / score.total) * 100)}%
            </div>
          </div>
        </div>

        {/* Question Navigation */}
        <div className="question-navigation">
          <button className="navigation-button" onClick={toggleNavigationModal}>
            <FontAwesomeIcon icon={faList} />
            <span>{getText("questionNavigation")}</span>
          </button>
          <div className="current-question-indicator">
            <span>{currentQuestion + 1}</span>
          </div>
        </div>

        {/* Navigation Modal */}
        <AnimatePresence>
          {showNavigationModal && (
            <motion.div
              className="navigation-modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowNavigationModal(false)}
            >
              <motion.div
                className="navigation-modal"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h3>{getText("questionNavigation")}</h3>
                </div>
                <div className="modal-content">
                  <div className="questions-grid">
                    {examData.map((question, index) => {
                      const status = getQuestionStatus(question);
                      return (
                        <button
                          key={index}
                          className={`question-number-button ${currentQuestion === index ? "active" : ""} ${status}`}
                          onClick={() => handleQuestionSelect(index)}
                          title={`${getText("question")} ${index + 1} - ${status === 'correct' ? getText("correctAnswer") : status === 'incorrect' ? getText("incorrectAnswer") : getText("notAnswered")}`}
                        >
                          {index + 1}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Question Container */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            className="question-container"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="question-header">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${((currentQuestion + 1) / examData.length) * 100}%`,
                  }}
                ></div>
              </div>
              <h3>
                {getText("question")} {currentQuestion + 1} {getText("from")} {examData.length}
              </h3>
            </div>

            <div className="question-content">
              <p className="question-text">
                {currentQuestionData?.question || getText("noQuestionText")}
              </p>

              {/* Question Weight Hint */}
              {currentQuestionData?.question_weight && currentQuestionData.question_weight !== 1 && (
                <motion.div
                  className="question-weight-hint"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2, duration: 0.4, ease: "easeOut" }}
                >
                  <div className="weight-icon-container">
                    <FontAwesomeIcon icon={faStar} className="weight-icon" />
                  </div>
                  <div className="weight-content">
                    <span className="weight-label">{getText("questionWeight")}</span>
                    <span className="weight-value">{currentQuestionData.question_weight}</span>
                    <span className="weight-hint-text">
                      {getText("questionWeightHint", currentQuestionData.question_weight)}
                    </span>
                  </div>
                  <div className="weight-glow"></div>
                </motion.div>
              )}

              <div className="options-container">
                {options && options.length > 0 ? (
                  options.map((option, index) => {
                    const hasAnswer = currentQuestionData.selected_answer !== null &&
                      currentQuestionData.selected_answer !== undefined &&
                      currentQuestionData.selected_answer !== 0;
                    const isSelected = hasAnswer && Number(currentQuestionData.selected_answer) === (index + 1);
                    const isCorrect = Number(currentQuestionData.correct_answer) === (index + 1);
                    const isIncorrect = isSelected && !isCorrect;

                    return (
                      <div
                        key={index}
                        className={`option ${isSelected ? "selected" : ""} ${isCorrect ? "correct" : ""} ${isIncorrect ? "incorrect" : ""}`}
                      >
                        <span className="option-number">
                          {String.fromCharCode(65 + index)}
                        </span>
                        <span className="option-text">{option}</span>

                        {/* Show correct answer icon */}
                        {isCorrect && (
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="result-icon correct"
                          />
                        )}

                        {/* Show incorrect answer icon */}
                        {isIncorrect && (
                          <FontAwesomeIcon
                            icon={faTimes}
                            className="result-icon incorrect"
                          />
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="no-options">
                    <p>{getText("noOptions")}</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Footer Navigation */}
        <div className="result-footer">
          <button
            className="nav-button next"
            onClick={handleNext}
            disabled={currentQuestion === examData.length - 1}
          >
            {getText("next")} <FontAwesomeIcon icon={faChevronRight} />
          </button>

          <button
            className="nav-button prev"
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
          >
            <FontAwesomeIcon icon={faChevronLeft} /> {getText("previous")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExamResult;
